import { useState, useEffect } from "react";
import axios from "axios";
import { Check, X, Star, EyeOff, Trash2, Flag, Shield, ShieldOff, AlertTriangle, Save, MessageSquare } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_TABS = [
  { id: "pending", name: "Pending", color: "text-amber-600" },
  { id: "flagged", name: "Flagged", color: "text-red-600" },
  { id: "published", name: "Published", color: "text-green-600" },
  { id: "featured", name: "Featured", color: "text-gold" },
  { id: "rejected", name: "Rejected", color: "text-gray-600" },
];

export default function InsightsManager() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [blockedWords, setBlockedWords] = useState("");
  const [bannedUsers, setBannedUsers] = useState([]);
  const [counts, setCounts] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/insights`, { params: tab ? { status: tab } : {}, withCredentials: true });
      setItems(r.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadAllCounts = async () => {
    try {
      const results = await Promise.all(STATUS_TABS.map(s =>
        axios.get(`${API}/api/admin/insights`, { params: { status: s.id }, withCredentials: true }).then(r => [s.id, r.data.length]).catch(() => [s.id, 0])
      ));
      const counts = Object.fromEntries(results);
      setCounts(counts);
    } catch {}
  };

  const loadSettings = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/insights/settings`, { withCredentials: true });
      setBlockedWords((r.data?.blocked_words || []).join(", "));
    } catch {}
  };

  const loadBanned = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/insights/banned-users`, { withCredentials: true });
      setBannedUsers(r.data || []);
    } catch {}
  };

  useEffect(() => { load(); loadAllCounts(); }, [tab]);
  useEffect(() => { loadSettings(); loadBanned(); }, []);

  const moderate = async (id, action, extra = {}) => {
    try {
      await axios.patch(`${API}/api/admin/insights/${id}`, { action, ...extra }, { withCredentials: true });
      load(); loadAllCounts();
    } catch (err) {
      alert(err?.response?.data?.detail || "Action failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await axios.delete(`${API}/api/admin/insights/${id}`, { withCredentials: true });
      load(); loadAllCounts();
    } catch { alert("Delete failed"); }
  };

  const saveBlockedWords = async () => {
    const words = blockedWords.split(/[,\n]/).map(w => w.trim()).filter(Boolean);
    try {
      await axios.put(`${API}/api/admin/insights/settings/blocked-words`, { words }, { withCredentials: true });
      alert(`Saved ${words.length} blocked words`);
    } catch (err) { alert(err?.response?.data?.detail || "Save failed"); }
  };

  const toggleBan = async (userId, banned) => {
    try {
      await axios.patch(`${API}/api/admin/users/${userId}/community-ban`, { banned }, { withCredentials: true });
      loadBanned();
    } catch (err) { alert(err?.response?.data?.detail || "Action failed"); }
  };

  return (
    <div className="space-y-6" data-testid="insights-manager">
      <div className="bg-white rounded-xl shadow-md p-5">
        <h2 className="font-playfair text-navy text-lg font-bold">Community Insights — Review Queue</h2>
        <p className="font-dm text-navy/60 text-xs">Moderate user-submitted posts. Featured posts surface on /blog as Community Spotlight.</p>
      </div>

      {/* Blocked Words */}
      <div className="bg-white rounded-xl shadow-md p-5" data-testid="blocked-words-section">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-4 h-4 text-red-500" />
          <h3 className="font-dm text-navy font-bold text-sm">Blocked Words (auto-reject on whole-word match)</h3>
        </div>
        <textarea value={blockedWords} onChange={e => setBlockedWords(e.target.value)} rows={2}
          placeholder="comma or newline separated, e.g. spam, scam, hate"
          data-testid="blocked-words-input"
          className="w-full border border-navy/20 rounded-lg px-3 py-2 font-mono text-xs" />
        <button onClick={saveBlockedWords} data-testid="save-blocked-words"
          className="mt-2 bg-navy text-gold font-dm font-bold text-xs px-4 py-2 rounded-lg flex items-center gap-2"><Save className="w-3 h-3" /> Save List</button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white rounded-lg p-1 w-fit shadow-sm flex-wrap">
        {STATUS_TABS.map(s => (
          <button key={s.id} onClick={() => setTab(s.id)} data-testid={`insights-tab-${s.id}`}
            className={`font-dm text-sm px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${tab === s.id ? "bg-navy text-white" : `${s.color} hover:text-navy`}`}>
            {s.name}
            {counts[s.id] > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tab === s.id ? "bg-white/20" : "bg-red-500 text-white"}`}>{counts[s.id]}</span>}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <p className="font-dm text-navy/40 text-sm text-center py-10">Loading...</p>
      ) : items.length === 0 ? (
        <p className="font-dm text-navy/40 text-sm text-center py-10">No posts in this view.</p>
      ) : (
        <div className="space-y-3">
          {items.map(p => (
            <div key={p._id} className="bg-white rounded-xl shadow-md p-5 flex gap-4" data-testid={`insight-card-${p._id}`}>
              {p.cover_image && (
                <img src={p.cover_image.startsWith("http") ? p.cover_image : `${API}${p.cover_image}`} alt="" className="w-24 h-24 object-cover rounded-lg flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <h4 className="font-dm text-navy font-bold text-sm line-clamp-1">{p.title}</h4>
                    <p className="font-dm text-navy/50 text-xs">{p.author_name} · {p.author_role || "Community Member"} · {p.author_email}</p>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {p.featured && <span className="font-dm text-[10px] bg-gold/20 text-gold px-2 py-0.5 rounded-full">Featured</span>}
                    <span className="font-dm text-[10px] bg-navy/5 text-navy px-2 py-0.5 rounded-full capitalize">{p.status}</span>
                    {p.auto_rejected && <span className="font-dm text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Auto: "{p.blocked_word}"</span>}
                    {p.report_count > 0 && (
                      <span className="font-dm text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full flex items-center gap-1"><Flag className="w-2.5 h-2.5" /> {p.report_count}</span>
                    )}
                  </div>
                </div>
                <p className="font-dm text-navy/70 text-xs mt-2 line-clamp-3">{p.body}</p>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {p.status !== "published" && p.status !== "featured" && (
                    <button onClick={() => moderate(p._id, "approve")} data-testid={`approve-${p._id}`}
                      className="bg-green-500 text-white font-dm text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-green-600 flex items-center gap-1"><Check className="w-3 h-3" /> Approve</button>
                  )}
                  {p.status !== "rejected" && (
                    <button onClick={() => moderate(p._id, "reject")} data-testid={`reject-${p._id}`}
                      className="bg-red-500 text-white font-dm text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-red-600 flex items-center gap-1"><X className="w-3 h-3" /> Reject</button>
                  )}
                  {!p.featured && (p.status === "published" || p.status === "pending" || p.status === "flagged") && (
                    <button onClick={() => moderate(p._id, "feature")} data-testid={`feature-${p._id}`}
                      className="bg-gold text-navy font-dm text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-gold/90 flex items-center gap-1"><Star className="w-3 h-3" /> Feature</button>
                  )}
                  {p.featured && (
                    <button onClick={() => moderate(p._id, "unfeature")} data-testid={`unfeature-${p._id}`}
                      className="bg-white border border-gold text-gold font-dm text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1"><Star className="w-3 h-3" /> Unfeature</button>
                  )}
                  {(p.status === "published" || p.status === "featured") && (
                    <button onClick={() => moderate(p._id, "unpublish")} data-testid={`unpublish-${p._id}`}
                      className="bg-navy/5 text-navy font-dm text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1"><EyeOff className="w-3 h-3" /> Unpublish</button>
                  )}
                  <button onClick={() => remove(p._id)} data-testid={`delete-${p._id}`}
                    className="text-red-500 hover:text-red-700 font-dm text-xs flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
                </div>
                <p className="font-dm text-navy/30 text-[10px] mt-2">Submitted {p.created_at ? new Date(p.created_at).toLocaleString("en-IN") : "-"}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Banned Users */}
      <div className="bg-white rounded-xl shadow-md p-5" data-testid="banned-users-section">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="w-4 h-4 text-red-500" />
          <h3 className="font-dm text-navy font-bold text-sm">Banned Users ({bannedUsers.length})</h3>
        </div>
        {bannedUsers.length === 0 ? (
          <p className="font-dm text-navy/40 text-xs">No banned users. Use the action below to ban a user by their User ID (find from Users tab).</p>
        ) : (
          <div className="space-y-2 mb-3">
            {bannedUsers.map(u => (
              <div key={u._id} className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid={`banned-${u._id}`}>
                <div>
                  <p className="font-dm text-navy text-sm font-semibold">{u.name}</p>
                  <p className="font-dm text-navy/60 text-xs">{u.email}</p>
                </div>
                <button onClick={() => toggleBan(u._id, false)} data-testid={`unban-${u._id}`}
                  className="bg-green-500 text-white font-dm text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1"><ShieldOff className="w-3 h-3" /> Unban</button>
              </div>
            ))}
          </div>
        )}
        <BanUserForm onDone={loadBanned} />
      </div>
    </div>
  );
}

function BanUserForm({ onDone }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const banByEmail = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      // Resolve user id via admin users list (lightweight grep)
      const r = await axios.get(`${API}/api/admin/users`, { withCredentials: true });
      const u = (r.data || []).find(x => (x.email || "").toLowerCase() === email.trim().toLowerCase());
      if (!u) { alert("User not found"); return; }
      await axios.patch(`${API}/api/admin/users/${u._id}/community-ban`, { banned: true }, { withCredentials: true });
      setEmail("");
      onDone();
    } catch (err) { alert(err?.response?.data?.detail || "Ban failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex gap-2 mt-2">
      <input value={email} onChange={e => setEmail(e.target.value)} placeholder="user@example.com" type="email"
        data-testid="ban-email-input"
        className="flex-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm" />
      <button onClick={banByEmail} disabled={busy} data-testid="ban-user-btn"
        className="bg-red-500 text-white font-dm text-sm font-bold px-4 py-2 rounded-lg flex items-center gap-1 disabled:opacity-50">
        <Shield className="w-3 h-3" /> Ban
      </button>
    </div>
  );
}
